import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../utils/constants';
import { fetchData } from '../pages/UserManagement/userManagementUtils/constants';
import useAuthToken from './useAuthToken';
import { useKeycloak } from '../context/KeycloakContext';
import { useAuth } from 'react-oidc-context';

type JobType = 'CREATE_USER';

const POLL_INTERVAL_MS = 5000;

/**
 * Shape of a background job stored in localStorage.
 */
interface BackgroundJob {
  type: JobType;
  key: string;
  expiresAt: number; // UNIX timestamp in ms (for local timeout)
}

const JOBS_STORAGE_KEY = 'backgroundJobs';

/**
 * A custom DOM event name that we'll dispatch whenever we update localStorage.
 */
const JOBS_UPDATED_EVENT = 'BACKGROUND_JOBS_UPDATED';

/**
 * Read the array of jobs from localStorage.
 */
function getJobsFromLocalStorage(): BackgroundJob[] {
  const stored = localStorage.getItem(JOBS_STORAGE_KEY);
  return stored ? (JSON.parse(stored) as BackgroundJob[]) : [];
}

/**
 * Write a new array of jobs to localStorage, then dispatch an event
 * so our hook knows there's a change.
 */
function saveJobsToLocalStorage(jobs: BackgroundJob[]): void {
  localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobs));
  window.dispatchEvent(new Event(JOBS_UPDATED_EVENT));
}

/**
 * Create a new job in localStorage, meaning it's "in progress" until removed.
 *
 * @param jobType
 * @param key
 * @param ttlMs  How many milliseconds until this job is considered expired locally.
 */
export function createLocalJob(type: JobType, key: string, ttlMs: number = 3 * 60 * 1000): void {
  const now = Date.now();
  const expiresAt = now + ttlMs;

  const jobs = getJobsFromLocalStorage();
  jobs.push({ type, key, expiresAt });
  saveJobsToLocalStorage(jobs);
}

export function removeLocalJob(type: JobType, key: string): void {
  const jobs = getJobsFromLocalStorage();
  const updated = jobs.filter((j) => !(j.type === type && j.key === key));
  saveJobsToLocalStorage(updated);
}

export function hasLocalJob(type: JobType, key: string): boolean {
  return getJobsFromLocalStorage().some((j) => j.type === type && j.key === key);
}

export function clearAllJobs(): void {
  localStorage.removeItem(JOBS_STORAGE_KEY);
  window.dispatchEvent(new Event(JOBS_UPDATED_EVENT));
}

/**
 * Example API to check job status. Adjust to your actual endpoint.
 *
 * @returns      "in-progress" or "finished"
 * @param job
 */

/**
 * Hook that:
 *  - Reads in-progress jobs from localStorage.
 *  - Every 5s:
 *    1) Removes any expired jobs (no snackbar).
 *    2) For each non-expired job, calls fetchJobStatus:
 *       - If "in-progress," keep it for the next poll.
 *       - If "finished," remove it and show a snackbar.
 *  - Automatically stops polling if there are no jobs left.
 *  - Resumes polling if a new job is added.
 *
 * Call this hook once in your <App /> to handle everything behind the scenes.
 */
export function useBackgroundTasks() {
  const auth = useAuth();
  const [jobs, setJobs] = useState<BackgroundJob[]>(() => getJobsFromLocalStorage());
  const { t } = useTranslation();
  const { mainAccountId } = useKeycloak();
  const token = useAuthToken();

  // We'll store the interval ID here so we can clear it
  const intervalRef = useRef<number | null>(null);

  const fetchJobStatus = useCallback(
    async (job: BackgroundJob): Promise<['in-progress' | 'finished', any | null]> => {
      switch (job.type) {
        case 'CREATE_USER': {
          const result = await new Promise<{ status: 'success' | 'error' | 'inprogress'; email?: string }>((resolve) => {
            fetchData(
              `${process.env.REACT_APP_GET_ACCOUNT_USERS}`,
              { accountId: mainAccountId, email: job.key, includeInactive: true },
              token,
              (response) => {
                if (response?.data?.length > 0) {
                  const actualEmail = response.data[0]?.emailAddress || job.key;
                  resolve({ status: 'success', email: actualEmail });
                } else {
                  resolve({ status: 'inprogress' });
                }
              },
              (error) => {
                if (error?.response && error.response.status >= 500) {
                  resolve({ status: 'error' });
                } else {
                  resolve({ status: 'inprogress' });
                }
              },
            );
          });

          if (result.status === 'success') {
            const toastArgs = {
              status: 'success',
              title: t('general.background_tasks.user_creation.snackbar_message_title'),
              text: t('general.background_tasks.user_creation.snackbar_message_success', { email: result.email }),
              hideClose: false,
            };
            return ['finished', toastArgs];
          } else if (result.status === 'error') {
            const toastArgs = {
              status: 'error',
              title: t('general.background_tasks.user_creation.snackbar_error_message_title'),
              text: t('general.background_tasks.user_creation.snackbar_error_message', { email: job.key }),
              hideClose: false,
            };
            return ['finished', toastArgs];
          } else {
            return ['in-progress', null];
          }
        }

        default:
          throw new Error(`Unknown job type: ${job.type}`);
      }
    },
    [mainAccountId, token, t],
  );

  /**
   * Listen for our custom event that means "the localStorage job list changed."
   * Sync that to our state so we stay up to date.
   */
  useEffect(() => {
    function handleJobsUpdated() {
      setJobs(getJobsFromLocalStorage());
    }

    window.addEventListener(JOBS_UPDATED_EVENT, handleJobsUpdated);
    return () => {
      window.removeEventListener(JOBS_UPDATED_EVENT, handleJobsUpdated);
    };
  }, []);

  /**
   * This function will run every 5 seconds while at least 1 job is present.
   * For each job:
   *  - If expired, remove it (no snackbar).
   *  - Else fetch the server status:
   *    - If "finished," remove it and showSnackbar.
   *    - Else keep it ("in-progress").
   */
  async function checkJobs() {
    if (!auth.isAuthenticated) return;
    const now = Date.now();
    const currentJobs = getJobsFromLocalStorage();
    let updatedJobs: BackgroundJob[] = [];
    let changed = false;

    for (const job of currentJobs) {
      // 1) Check local expiration
      if (now >= job.expiresAt) {
        changed = true;
        const toastArgs = {
          status: 'error',
          title: t('general.background_tasks.user_creation.snackbar_error_message_title'),
          text: t('general.background_tasks.user_creation.snackbar_error_message', { email: job.key }),
          hideClose: false,
        };
        toast(toastArgs, null);
      } else {
        // 2) It's not expired, so see if it's finished on the server
        try {
          const [status, toastArgs] = await fetchJobStatus(job);
          if (status === 'finished') {
            toast(toastArgs, null);
            changed = true;
          } else {
            // status === 'in-progress'
            updatedJobs.push(job);
          }
        } catch (error) {
          console.error(`Error fetching status for job ${JSON.stringify(job)}:`, error);
          // We'll keep the job in place if there's an error
          updatedJobs.push(job);
        }
      }
    }

    // If we removed or finished any jobs, persist the new list
    if (changed) {
      saveJobsToLocalStorage(updatedJobs);
    }
  }

  useEffect(() => {
    if (!auth.isAuthenticated) {
      clearAllJobs();
    }
  }, [auth]);
  /**
   * Start or stop the polling interval depending on whether we have jobs.
   */
  useEffect(() => {
    const hasJobs = jobs.length > 0;

    if (hasJobs && intervalRef.current === null) {
      const id = window.setInterval(checkJobs, POLL_INTERVAL_MS);
      intervalRef.current = id as unknown as number; // TS quirk
    }

    if (!hasJobs && intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [auth, jobs]);

  return { jobs };
}
